import { useNavigate, useSearch } from "@tanstack/react-location"
import { exists } from "utils"
import { useStableCallback } from "./useStableCallback"

export type SetQueryParam<T> = (
  value: T | undefined | ((_value: T | undefined) => T | undefined)
) => void

export const useQueryParam = <T extends string = string>(
  key: string
): [T | undefined, SetQueryParam<T>] => {
  const params = useSearch()
  const navigate = useNavigate()

  const getSearchParams = (value: T | undefined) => {
    return exists(value) && value !== ""
      ? { ...params, [key]: value }
      : { ...params, [key]: undefined }
  }

  const setValue = useStableCallback<SetQueryParam<T>>((newValue) => {
    if (typeof newValue === "function") {
      const existingValue = toString(params[key]) as T | undefined
      newValue = newValue(existingValue)
    }

    navigate({
      replace: true,
      search: getSearchParams(newValue),
    })
  })

  const value = toString(params[key]) as T | undefined
  return [value, setValue]
}

/**
 * Hook to get and set multiple query parameters at once as an object.
 * Sometimes we need to change multiple query params at the same time,
 * and if we try to change them one at a time using useQueryParam,
 * we will run into race condition where only one of the params is changed.
 */
export const useQueryParams = <T extends Record<string, unknown>>() => {
  const queryParams = useSearch()
  const navigate = useNavigate()

  const setQueryParams = useStableCallback((newValue) => {
    if (typeof newValue === "function") {
      newValue = newValue(queryParams as T)
    }

    navigate({
      replace: true,
      search: newValue,
    })
  })

  return { queryParams, setQueryParams } as {
    queryParams: T
    setQueryParams: SetQueryParam<T>
  }
}

const toString = (value: unknown) => {
  if (value === undefined) return
  if (typeof value === "string") return value
  return String(value)
}
